import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  BookOpen,
  FileText,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Building2,
  Calendar,
  TrendingUp,
  TrendingDown,
  DollarSign
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { Badge } from "../../../components/ui/badge";
import { useAuth } from "../../../context/AuthContext";

// Sub-components
import PlanCuentas from "./PlanCuentas";
import Asientos from "./Asientos";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TENANT_ID = "cao";

const Contabilidad = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [empresa, setEmpresa] = useState(null);
  const [ejercicios, setEjercicios] = useState([]);
  const [ejercicioActivo, setEjercicioActivo] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");

  const fetchEmpresa = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}`);
      setEmpresa(response.data);
    } catch (error) {
      console.error("Error al cargar empresa:", error);
    }
  };

  const fetchEjercicios = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/erp/empresas/${TENANT_ID}/ejercicios`);
      setEjercicios(response.data);
      if (response.data.length > 0) {
        setEjercicioActivo(response.data[0]);
      }
    } catch (error) {
      console.error("Error al cargar ejercicios:", error);
    }
  };

  const fetchResumen = async () => {
    if (!ejercicioActivo) return;
    try {
      const response = await axios.get(
        `${API_URL}/api/erp/empresas/${TENANT_ID}/ejercicios/${ejercicioActivo.id}/sumas-saldos?solo_con_movimientos=true`
      );
      setResumen(response.data);
    } catch (error) {
      console.error("Error al cargar resumen:", error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchEmpresa(), fetchEjercicios()]);
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    if (ejercicioActivo) {
      fetchResumen();
    }
  }, [ejercicioActivo]);

  const calcularTotales = () => {
    if (!resumen || !resumen.lineas) return { activo: 0, pasivo: 0, ingresos: 0, egresos: 0 };
    
    let activo = 0, pasivo = 0, ingresos = 0, egresos = 0;
    
    resumen.lineas.forEach(linea => {
      if (linea.tipo_cuenta === "ACTIVO") activo += linea.saldo_deudor - linea.saldo_acreedor;
      if (linea.tipo_cuenta === "PASIVO") pasivo += linea.saldo_acreedor - linea.saldo_deudor;
      if (linea.tipo_cuenta === "INGRESO") ingresos += linea.saldo_acreedor - linea.saldo_deudor;
      if (linea.tipo_cuenta === "EGRESO") egresos += linea.saldo_deudor - linea.saldo_acreedor;
    });
    
    return { activo, pasivo, ingresos, egresos };
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 2
    }).format(value);
  };

  const totales = calcularTotales();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2853B9]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="erp-contabilidad">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3876]">Contabilidad</h1>
          <p className="text-gray-600">
            {empresa?.nombre} - {ejercicioActivo?.nombre || "Sin ejercicio activo"}
          </p>
        </div>
        <div className="flex gap-2">
          {ejercicios.length > 0 && (
            <select
              className="cao-input w-auto"
              value={ejercicioActivo?.id || ""}
              onChange={(e) => {
                const ej = ejercicios.find(x => x.id === parseInt(e.target.value));
                setEjercicioActivo(ej);
              }}
              data-testid="select-ejercicio"
            >
              {ejercicios.map((ej) => (
                <option key={ej.id} value={ej.id}>{ej.nombre}</option>
              ))}
            </select>
          )}
          <Button
            variant="outline"
            size="icon"
            onClick={() => { fetchEjercicios(); fetchResumen(); }}
            data-testid="btn-refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-white border shadow-sm">
          <TabsTrigger value="dashboard" className="data-[state=active]:bg-[#2853B9] data-[state=active]:text-white">
            <Building2 className="w-4 h-4 mr-2" />
            Resumen
          </TabsTrigger>
          <TabsTrigger value="cuentas" className="data-[state=active]:bg-[#2853B9] data-[state=active]:text-white">
            <BookOpen className="w-4 h-4 mr-2" />
            Plan de Cuentas
          </TabsTrigger>
          <TabsTrigger value="asientos" className="data-[state=active]:bg-[#2853B9] data-[state=active]:text-white">
            <FileText className="w-4 h-4 mr-2" />
            Asientos
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6 mt-6">
          {/* KPIs */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="cao-card border-l-4 border-l-blue-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Activo</p>
                      <p className="text-xl font-bold text-[#1E3876]">{formatCurrency(totales.activo)}</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card className="cao-card border-l-4 border-l-red-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Pasivo</p>
                      <p className="text-xl font-bold text-[#1E3876]">{formatCurrency(totales.pasivo)}</p>
                    </div>
                    <DollarSign className="w-8 h-8 text-red-500" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card className="cao-card border-l-4 border-l-green-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Ingresos</p>
                      <p className="text-xl font-bold text-[#1E3876]">{formatCurrency(totales.ingresos)}</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <Card className="cao-card border-l-4 border-l-orange-500">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Egresos</p>
                      <p className="text-xl font-bold text-[#1E3876]">{formatCurrency(totales.egresos)}</p>
                    </div>
                    <TrendingDown className="w-8 h-8 text-orange-500" />
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Resultado */}
          <Card className="cao-card">
            <CardHeader>
              <CardTitle className="text-[#1E3876]">Resultado del Periodo</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <p className="text-sm text-gray-500 mb-2">Resultado (Ingresos - Egresos)</p>
                  <p className={`text-4xl font-bold ${totales.ingresos - totales.egresos >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {formatCurrency(totales.ingresos - totales.egresos)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Info Empresa */}
          {empresa && (
            <Card className="cao-card">
              <CardHeader>
                <CardTitle className="text-[#1E3876]">Datos de la Empresa</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Razon Social</p>
                    <p className="font-medium">{empresa.nombre}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">CUIT</p>
                    <p className="font-medium">{empresa.cuit || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Direccion</p>
                    <p className="font-medium">{empresa.direccion || "-"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{empresa.email || "-"}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Plan de Cuentas Tab */}
        <TabsContent value="cuentas" className="mt-6">
          <PlanCuentas tenantId={TENANT_ID} />
        </TabsContent>

        {/* Asientos Tab */}
        <TabsContent value="asientos" className="mt-6">
          {ejercicioActivo ? (
            <Asientos tenantId={TENANT_ID} ejercicioId={ejercicioActivo.id} />
          ) : (
            <Card className="cao-card">
              <CardContent className="p-8 text-center">
                <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Seleccione un ejercicio para ver los asientos</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Contabilidad;
